/**
 * 화면 한 장 — 항목 하나를 실제 비율(16:9)로 그린다.
 *
 * 모니터링 카드와 화면 상세가 **같은 그림**을 보여줘야 한다. 두 곳이 다르게
 * 그리면 "카드에서는 이게 보이는데 상세에서는 저게 보인다"가 되고, 그 순간
 * 둘 다 못 믿게 된다.
 *
 * 종류마다 보여줄 수 있는 것이 다르다 — 없는 것을 있는 척하지 않는다.
 */
import type { ReactNode } from 'react';
import { FileCode, Globe, Image as ImageIcon, MonitorX, Video } from 'lucide-react';
import type { ResolvedItem, ScreenReport } from '../api/client.js';
import { FIRST_SCENE_SEC, LiveScreen, VideoStill } from './LiveScreen.js';
import { TemplatePreview } from './TemplatePreview.js';
import { HtmlFrame } from './HtmlContent.js';

export function ScreenFrame({
  item,
  location,
  timezone,
  /** 크게 그리는 자리인가. 영상의 첫 장면·웹 페이지 링크를 붙인다. 작은 카드에서는 끈다. */
  interactive = false,
  /**
   * 화면이 실제로 올려 보낸 그림.
   *
   * 있으면 **이것이 먼저다.** 자산 미리보기는 "이것이 나가야 한다"이고 이것은
   * "화면에 이것이 떠 있다"다. 지금 보고 있는 장이 화면이 보고한 그 장일 때만
   * 넘어온다 — 운영자가 다른 장으로 넘긴 뒤에도 같은 그림을 붙이면 거짓말이 된다.
   */
  shot = null,
  /** 찍어 둔 그림이 서버에서 이미 지워졌다. 부르는 쪽이 새로 찍는다. */
  onShotMissing,
}: {
  item: ResolvedItem | null;
  location?: string;
  timezone?: string;
  interactive?: boolean;
  shot?: ScreenReport | null;
  onShotMissing?: (report: ScreenReport) => void;
}): ReactNode {
  if (shot) {
    // 영상이면 같은 영상에서 화면이 보고한 위치의 장면을 멈춘 채 건다 — 화면이 보낸 것은 위치뿐이다.
    return <LiveScreen report={shot} videoUrl={item?.preview_url ?? null} onMissing={onShotMissing} />;
  }

  if (!item) {
    return (
      <Blank tone="warn">
        <MonitorX className="h-7 w-7" strokeWidth={1.5} />
        <span>재생할 항목이 없습니다</span>
      </Blank>
    );
  }

  if (item.type === 'template' && item.template) {
    return (
      <TemplatePreview template={item.template} location={location ?? '미리보기'} timezone={timezone} live />
    );
  }

  if (item.type === 'image' && item.preview_url) {
    return (
      <img
        src={item.preview_url}
        alt={item.title ?? ''}
        className="h-full w-full object-contain"
        loading="lazy"
      />
    );
  }

  if (item.type === 'video' && item.preview_url) {
    /*
     * **틀지 않는다. 첫 장면을 그림으로 건다.**
     *
     * 여기로 오는 것은 찍어 둔 그림이 없을 때다 — 다른 장을 넘겨 보거나, 다른 시각을
     * 골랐거나, 화면이 찍어 보내지 못했을 때. 이 자리에 틀 수 있는 영상을 걸면 상세가
     * "화면을 찍어 보여주는 곳" 이 아니라 영상 플레이어가 된다.
     */
    return interactive ? (
      <VideoStill frameKey={`${item.assetId}@first`} url={item.preview_url} atSec={FIRST_SCENE_SEC} />
    ) : (
      <Blank tone="dark">
        <Video className="h-7 w-7" strokeWidth={1.5} />
        <span className="line-clamp-2 px-4 text-center">{item.title ?? '영상'}</span>
      </Blank>
    );
  }

  if (item.type === 'web') {
    /*
     * 외부 페이지를 콘솔 안에서 실행하지 않는다 — 그 페이지의 스크립트에
     * 콘솔이 노출된다. 주소만 보여주고 새 창으로 넘긴다.
     */
    return (
      <Blank tone="dark">
        <Globe className="h-7 w-7" strokeWidth={1.5} />
        <span className="line-clamp-2 px-4 text-center">{item.title ?? '웹 페이지'}</span>
        {interactive ? (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[11.5px] text-white/70 underline"
            onClick={(event) => event.stopPropagation()}
          >
            새 창에서 열기
          </a>
        ) : null}
      </Blank>
    );
  }

  if (item.type === 'html') {
    /*
     * 올린 HTML 은 서버가 출처 없는 샌드박스 문서로 내주는 주소가 있어 줄여 띄운다 — 콘솔에 닿지 못한다(`HtmlContent.tsx`).
     * 웹 페이지는 그런 울타리가 없어 띄우지 않는다(위). 화면이 찍어 보낸 그림이 있으면 위에서 그것을 먼저 건다.
     */
    if (item.preview_url) {
      return (
        <HtmlFrame
          url={item.preview_url}
          title={item.title ?? 'HTML 콘텐츠'}
          className="h-full w-full bg-black"
          design={item.width && item.height ? { width: item.width, height: item.height } : null}
          fit={item.fit}
        />
      );
    }
    return (
      <Blank tone="dark">
        <FileCode className="h-7 w-7" strokeWidth={1.5} />
        <span className="line-clamp-2 px-4 text-center">{item.title ?? 'HTML 콘텐츠'}</span>
      </Blank>
    );
  }

  return (
    <Blank tone="dark">
      <ImageIcon className="h-7 w-7" strokeWidth={1.5} />
      <span className="line-clamp-2 px-4 text-center">{item.title ?? '미리볼 수 없습니다'}</span>
    </Blank>
  );
}

function Blank({ tone, children }: { tone: 'warn' | 'dark'; children: ReactNode }): ReactNode {
  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center gap-2 text-[12.5px] ${
        tone === 'warn' ? 'bg-amber-50 text-amber-700' : 'bg-slate-800 text-slate-300'
      }`}
    >
      {children}
    </div>
  );
}
